import { Body, Controller, Delete, ForbiddenException, Get, HttpCode, HttpStatus, Param, Patch, Post, Query, Req, UseGuards } from '@nestjs/common';
import { AgentJwtGuard, AgentScopedRequest } from '../common/guards/agent-jwt.guard';
import { CreateKnowledgeEntryDto, UpdateKnowledgeEntryDto } from './dto/knowledge-entry.dto';
import { KnowledgeService } from './knowledge.service';

// FAQ authoring for the store's own bot — same auth as the rest of the
// agent-facing surface (AgentJwtGuard: the admin's own cart-platform login,
// routed to their store's application). A separate controller from
// AgentController on purpose — this is a distinct resource (content, not
// conversations/tickets), not another action on a session or SR.
@UseGuards(AgentJwtGuard)
@Controller('agent/knowledge')
export class KnowledgeController {
  constructor(private readonly knowledge: KnowledgeService) {}

  @Get()
  list(@Req() request: AgentScopedRequest) {
    return this.knowledge.list(this.storeApplicationId(request));
  }

  // Surfaces the bot's real gaps — questions the scripted responder scored
  // below UNCERTAIN_THRESHOLD for, so the merchant knows what to author next.
  @Get('unanswered')
  listUnanswered(@Req() request: AgentScopedRequest, @Query('limit') limit?: string) {
    const parsed = limit ? Number(limit) : undefined;
    return this.knowledge.listUnanswered(this.storeApplicationId(request), parsed && !Number.isNaN(parsed) ? parsed : undefined);
  }

  @Post()
  create(@Req() request: AgentScopedRequest, @Body() dto: CreateKnowledgeEntryDto) {
    return this.knowledge.create(this.storeApplicationId(request), dto);
  }

  @Patch(':id')
  update(@Req() request: AgentScopedRequest, @Param('id') id: string, @Body() dto: UpdateKnowledgeEntryDto) {
    return this.knowledge.update(this.storeApplicationId(request), id, dto);
  }

  @Delete(':id')
  @HttpCode(HttpStatus.NO_CONTENT)
  remove(@Req() request: AgentScopedRequest, @Param('id') id: string) {
    return this.knowledge.remove(this.storeApplicationId(request), id);
  }

  // FAQ content is store-specific business content, not a platform-operator
  // concern — a platform-wide agent (applicationId: null, e.g. platform_admin)
  // has no single store's knowledge base to manage here.
  private storeApplicationId(request: AgentScopedRequest): string {
    if (!request.agent.applicationId) {
      throw new ForbiddenException('Select a specific store to manage its FAQ content');
    }
    return request.agent.applicationId;
  }
}
